import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MultiSegmentProgressBarComponent } from '../../common/multi-segment-progress-bar/multi-segment-progress-bar.component';
import { StatusBadgeComponent } from '../../common/status-badge/status-badge.component';

export interface ExecutionCycle {
  id: string;
  name: string;
  automationProjectId?: string;
  executionState: string;
  status: string;
  totalCount: number;
  completedCount: number;
  executingCount: number;
  waitingCount: number;
  stoppedCount: number;
  passedCount: number;
  failedCount: number;
  abortedCount: number;
  startTime?: string;
  endTime?: string;
  children?: any[];
}

@Component({
  selector: 'app-execution-cycle-card',
  standalone: true,
  imports: [CommonModule, MultiSegmentProgressBarComponent, StatusBadgeComponent],
  templateUrl: './execution-cycle-card.component.html',
  styleUrl: './execution-cycle-card.component.scss'
})
export class ExecutionCycleCardComponent {
  @Input() cycle!: ExecutionCycle;
  @Input() badge?: string;
  @Input() showCheckbox: boolean = true;
  @Input() isChecked: boolean = false;

  @Output() checkboxChange = new EventEmitter<{ id: string; checked: boolean }>();
  @Output() viewDetails = new EventEmitter<string>();

  onCheckboxChange(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.checkboxChange.emit({ id: this.cycle.id, checked });
  }

  onViewDetails(): void {
    this.viewDetails.emit(this.cycle.id);
  }

  formatDate(dateString?: string): string {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return 'N/A';

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);

    const hours24 = date.getHours();
    const hours12 = hours24 % 12 || 12;
    const hour = String(hours12).padStart(2, '0');
    const minute = String(date.getMinutes()).padStart(2, '0');
    const second = String(date.getSeconds()).padStart(2, '0');
    const meridiem = hours24 >= 12 ? 'pm' : 'am';

    return `${day}/${month}/${year}, ${hour}:${minute}.${second} ${meridiem}`;
  }

  calculateDuration(): string {
    
    if (!this.cycle.startTime || !this.cycle.endTime) return 'N/A';
    const start = new Date(this.cycle.startTime).getTime();
    const end = new Date(this.cycle.endTime).getTime();
    const diff = end - start;
    if (diff < 0) return 'N/A';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    return `${minutes}m ${seconds}s`;
  }

  get completedLabel(): string {
    return `${this.cycle.completedCount} of ${this.cycle.totalCount} completed`;
  }
}
